import {
  PublicCreateCustomerTransactionInquiryResponse,
  PublicCreateCustomerTransactionResponse,
  PublicCreateCustomerTransactionAuthResponse,
} from '@bukalapak/openapi-schema/vptrader/transaction/v2/public/transaction-domain';
import { PublicListCustomerTransactionsResponse } from '@bukalapak/openapi-schema/vptrader/transaction/v1/public/transaction-domain';
import VPNonTraderAPIClient from '~/api/vpnontrader';
import { getErrorMessage } from '~/store/modules/error-handler';
import { getInquiryCustomErrorMessage } from '~/utils/buka20-vp-transaction-utils';
import { TRANSACTION_GENERAL_ERROR_MESSAGE } from '~/components/Buka20/constant';

const initialState = {
  customerTransactionAuth: {},
  customerTransactionInquiry: {},
  customerTransactionInquiryLoading: false,
  customerTransactionInquiryErrorMessage: '',
  customerTransaction: {},
  customerTransactionLoading: false,
  previousTransactions: [],
  previousTransactionsLoading: false,
};

const getters = {
  getCustomerTransactionAuth(state) {
    return state.customerTransactionAuth;
  },
  getCustomerTransactionInquiry(state) {
    return state.customerTransactionInquiry;
  },
  getCustomerTransactionInquiryLoading(state) {
    return state.customerTransactionInquiryLoading;
  },
  getCustomerTransactionInquiryErrorMessage(state) {
    return state.customerTransactionInquiryErrorMessage;
  },
  getCustomerTransaction(state) {
    return state.customerTransaction;
  },
  getCustomerTransactionLoading(state) {
    return state.customerTransactionLoading;
  },
  getPreviousTransactions(state) {
    return state.previousTransactions;
  },
  getPreviousTransactionsLoading(state) {
    return state.previousTransactionsLoading;
  },
};

const actions = {
  async createCustomerTransactionAuth({ dispatch, commit }, requestPayload) {
    let response: PublicCreateCustomerTransactionAuthResponse = {};

    try {
      response = await VPNonTraderAPIClient.createCustomerTransactionAuth(requestPayload);
      commit('setCustomerTransactionAuth', response.customer_transaction_auth);
      return response.customer_transaction_auth;
    } catch {
      dispatch('catchError', { message: TRANSACTION_GENERAL_ERROR_MESSAGE });
    }
  },
  async createCustomerTransactionInquiry({ dispatch, commit }, requestPayload) {
    let response: PublicCreateCustomerTransactionInquiryResponse = {};

    try {
      commit('setCustomerTransactionInquiryLoading', true);

      response = await VPNonTraderAPIClient.createCustomerTransactionInquiry(requestPayload);
      commit('setCustomerTransactionInquiry', response.customer_transaction_inquiry);
      commit('setCustomerTransactionInquiryErrorMessage', '');
      return response.customer_transaction_inquiry;
    } catch (error) {
      const customErrorMessage = getInquiryCustomErrorMessage(error);
      if (customErrorMessage) {
        commit('setCustomerTransactionInquiryErrorMessage', customErrorMessage);
      } else {
        commit('setCustomerTransactionInquiryErrorMessage', '');
        dispatch('catchError', { message: TRANSACTION_GENERAL_ERROR_MESSAGE });
      }
      throw error;
    } finally {
      commit('setCustomerTransactionInquiryLoading', false);
    }
  },
  async createCustomerTransaction({ dispatch, commit }, payload) {
    let response: PublicCreateCustomerTransactionResponse = {};
    try {
      commit('setCustomerTransactionLoading', true);

      response = await VPNonTraderAPIClient.createCustomerTransaction(payload);
      commit('setCustomerTransaction', response.customer_transaction);
      return response.customer_transaction;
    } catch (error) {
      if (error?.errors?.[0].code === 'TRANSACTION_VOUCHER_OUT-OF-QUOTA') {
        return { error: 'TRANSACTION_VOUCHER_OUT-OF-QUOTA' };
      }

      const message = getErrorMessage(error);
      if (message?.trim() === 'duplicate order id') {
        dispatch('catchError', {
          message: 'Duplicate order id',
        });
        return;
      }

      dispatch('catchError', { error });
    } finally {
      commit('setCustomerTransactionLoading', true);
    }

    return response;
  },

  async listPreviousTransactions({ dispatch, commit }, requestPayload) {
    let response: PublicListCustomerTransactionsResponse = {};
    try {
      commit('setPreviousTransactionsLoading', true);
      response = await VPNonTraderAPIClient.listTransactions(requestPayload);
      commit('setPreviousTransactions', response.transactions);
    } catch (error) {
      dispatch('catchError', { message: 'Gagal mendapatkan data transaksi sebelumnya' });
    } finally {
      commit('setPreviousTransactionsLoading', false);
    }
    return response;
  },
};

const mutations = {
  setCustomerTransactionAuth(state, customerTransactionAuth) {
    state.customerTransactionAuth = {
      ...customerTransactionAuth,
    };
  },
  setCustomerTransactionInquiry(state, customerTransactionInquiry) {
    state.customerTransactionInquiry = {
      ...customerTransactionInquiry,
    };
  },
  setCustomerTransactionInquiryLoading(state, isLoading) {
    state.customerTransactionInquiryLoading = isLoading;
  },
  setCustomerTransactionInquiryErrorMessage(state, errorMessage) {
    state.customerTransactionInquiryErrorMessage = errorMessage;
  },

  setCustomerTransaction(state, customerTransaction) {
    state.customerTransaction = {
      ...customerTransaction,
    };
  },
  setCustomerTransactionLoading(state, isLoading) {
    state.customerTransactionLoading = isLoading;
  },
  setPreviousTransactions(state, previousTransactions) {
    state.previousTransactions = previousTransactions;
  },
  setPreviousTransactionsLoading(state, isLoading) {
    state.previousTransactionsLoading = isLoading;
  },
  resetState(state) {
    state.customerTransactionAuth = JSON.parse(JSON.stringify(initialState.customerTransactionAuth));
    state.customerTransactionInquiry = JSON.parse(JSON.stringify(initialState.customerTransactionInquiry));
    state.customerTransactionInquiryLoading = JSON.parse(
      JSON.stringify(initialState.customerTransactionInquiryLoading)
    );
    state.customerTransaction = JSON.parse(JSON.stringify(initialState.customerTransaction));
    state.customerTransactionLoading = JSON.parse(JSON.stringify(initialState.customerTransactionLoading));
    state.previousTransactions = JSON.parse(JSON.stringify(initialState.previousTransactions));
    state.previousTransactionsLoading = JSON.parse(JSON.stringify(initialState.previousTransactionsLoading));
  },
};

export default {
  state: () => ({ ...initialState }),
  mutations,
  getters,
  actions,
};
